import {Suspense, type PropsWithChildren} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {ForYouItemError, ForYouItemSkeleton} from './ForYouItemFallbacks'
import {useReviewers} from '../../data/use-reviewers'
import {useViewerRoles} from './use-viewer-roles'

export type ForYouAudience = 'author' | 'reviewer'

function isViewerReviewer(
  reviewers: NonNullable<ReturnType<typeof useReviewers>['data']>,
  viewerLogin: string,
): boolean {
  const isViewer = (login: string | undefined) => login === viewerLogin

  return (
    (reviewers.pendingReviewRequests ?? []).some(
      request => request.reviewer?.reviewerType === 'User' && isViewer(request.reviewer.displayLogin),
    ) ||
    (reviewers.completedReviews ?? []).some(
      review => review.reviewer?.reviewerType === 'User' && isViewer(review.reviewer.displayLogin),
    )
  )
}

/**
 * Whether the current viewer "owns" the pull request — either as its author or as an assignee.
 *
 * Being an assignee covers scenarios like:
 *    - the PR is authored by a bot or other non-human account, and the viewer is the human responsible for it.
 *   - the PR is authored by a separate human, but the viewer is taking over responsibility for shipping it (e.g., the author is on vacation).
 */
export function useViewerOwnsPullRequest(): boolean | null {
  const {isAuthor, isAssignee, queryStatus} = useViewerRoles()
  if (queryStatus !== 'success') return null

  return isAuthor || isAssignee
}

/**
 * Whether the current viewer is a "reviewer" of the pull request — i.e., they have
 * either an outstanding (pending) review request or a completed review, in any state
 * (approved, commented, changes requested, or dismissed).
 *
 * Only considers the viewer's own user-level participation; team review requests are
 * not treated as making the viewer an individual reviewer.
 */
export function useViewerIsReviewer(): boolean | null {
  const viewerLogin = getCurrentUserLogin()
  const {data: reviewers} = useReviewers()

  if (!viewerLogin) return false
  if (!reviewers) return null

  return isViewerReviewer(reviewers, viewerLogin)
}

/**
 * Error boundary + suspense wrapper for a single ForYou item. When `audience` is
 * provided, the item only renders for the matching viewer; otherwise it always
 * renders. The `author` audience includes assignees; the `reviewer` audience is
 * anyone with a pending review request or a completed review (in any state).
 */
export function ForYouItemBoundary({
  children,
  name,
  audience,
}: PropsWithChildren<{name: string; audience?: ForYouAudience}>) {
  const item = audience ? <AudienceGate audience={audience}>{children}</AudienceGate> : children

  return (
    <ErrorBoundary boundaryName={`ForYouItem.${name}`} fallback={<ForYouItemError />}>
      <Suspense fallback={<ForYouItemSkeleton />}>{item}</Suspense>
    </ErrorBoundary>
  )
}

/** Gates on whether the viewer "owns" the pull request (author or assignee) or is a reviewer. */
function AudienceGate({audience, children}: PropsWithChildren<{audience: ForYouAudience}>) {
  const viewerRoles = useViewerRoles()
  const {data: reviewers, isError: reviewersError, isLoading: reviewersLoading} = useReviewers()

  if (viewerRoles.queryStatus === 'loading' || reviewersLoading) {
    return <ForYouItemSkeleton />
  }

  if (viewerRoles.queryStatus === 'error' || (reviewersError && !reviewers)) {
    return <ForYouItemError />
  }

  if (viewerRoles.queryStatus === 'missing' || !reviewers) {
    return null
  }

  const viewerIsOwner = viewerRoles.isAuthor || viewerRoles.isAssignee
  const viewerLogin = getCurrentUserLogin()
  const viewerIsReviewer = !!viewerLogin && isViewerReviewer(reviewers, viewerLogin)
  const matchesAudience = audience === 'author' ? viewerIsOwner : viewerIsReviewer

  return matchesAudience ? <>{children}</> : null
}
